import { Component, computed, DestroyRef, inject, output, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { InputLabel } from '../../../../parts/input-label/input-label.component';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { ChargesService } from '../../../../core/services/charge/charge.service';
import { PropertyChargeModel, UnmarkPropertyChargeRequest } from '../../../../core/models/data/property-charge.model';
import { PaymentHistoryModel } from '../../../../core/models/data/payment-history.model';
import { BalancesMessagingConstants, GenericMessagingConstants } from '../../../../core/constants/messaging.constants';
import { BaseFormComponent } from '../../../../core/base-classes/base-form-component';
import { EscCloseDirective } from '../../../../core/directives/esc-close.directive';
import { CanComponentDeactivate } from '../../../../core/routing/can-deactivate.guard';
import { DirtyFormRegistryService } from '../../../../core/services/dirty-form-registry.service';

@Component({
	selector: 'ms-unmark-payment-modal',
	imports: [
		CommonModule,
		ReactiveFormsModule,
		FontAwesomeModule,
		InputLabel,
		ConfirmationModalComponent,
		InfoModalComponent,
		EscCloseDirective
	],
	templateUrl: './unmark-payment-modal.component.html'
})
export class UnmarkPaymentModalComponent extends BaseFormComponent implements CanComponentDeactivate {

	//#region Properties & Signals

	public unmarkCompleted = output<PropertyChargeModel>();

	public chargeId = signal<number | undefined>(undefined);
	public payment = signal<PaymentHistoryModel | undefined>(undefined);

	public confirmationMessage = computed(() =>
		BalancesMessagingConstants.UNMARK_PAYMENT_CONFIRMATION()
			.replaceValuesWithObject({ invoiceId: this.chargeId() })
	);

	public unmarkForm: FormGroup;

	@ViewChild('unmarkPaymentInfoRef') infoRef!: InfoModalComponent;
	@ViewChild('unmarkPaymentEscConfirmationRef') escConfirmationRef!: ConfirmationModalComponent;

	//#endregion Properties & Signals

	//#region Constructor

	private fb = inject(FormBuilder);
	private chargesService = inject(ChargesService);
	private bootstrapService = inject(BootstrapService);
	private dirtyFormRegistry = inject(DirtyFormRegistryService);
	private destroyRef = inject(DestroyRef);

	constructor() {
		super();

		this.destroyRef.onDestroy(() => this.dirtyFormRegistry.unregister(this));

		this.unmarkForm = this.fb.group({
			note: ['', [Validators.maxLength(255)]],
			sendEmail: [true]
		});
	}

	//#endregion Constructor

	//#region Public Methods

	public open(chargeId: number, payment: PaymentHistoryModel): void {
		this.chargeId.set(chargeId);
		this.payment.set(payment);
		this.resetForm();
		this.dirtyFormRegistry.register(this);
		this.bootstrapService.manageModal('#unmarkPaymentModal', ModalActions.Show);
	}

	//#endregion Public Methods

	//#region Form Submission

	public async onSubmit(): Promise<void> {
		if (this.unmarkForm.invalid) {
			this.unmarkForm.markAllAsTouched();
			return;
		}

		const chargeId = this.chargeId();
		if (chargeId === undefined) return;

		this.isSubmitting = true;

		try {
			const request = this.buildRequest();
			const result = await this.chargesService.unmarkPropertyCharge(chargeId, request);

			this.bootstrapService.manageModal('#unmarkPaymentModal', ModalActions.Hide);

			if (result.success && result.data) {
				this.submitSucceeded = true;
				this.infoRef.modalType.set(ModalTypes.Success);
				this.infoRef.message.set(
					BalancesMessagingConstants.UNMARK_PAYMENT_SUCCESS().replace('{invoiceId}', String(chargeId))
				);
				this.bootstrapService.manageModal('#unmarkPaymentInfoModal', ModalActions.Show);
				this.unmarkCompleted.emit(result.data);
			} else {
				this.infoRef.modalType.set(ModalTypes.Failure);
				this.infoRef.message.set(result.message || BalancesMessagingConstants.UNMARK_PAYMENT_FAILURE());
				this.bootstrapService.manageModal('#unmarkPaymentInfoModal', ModalActions.Show);
			}
		} catch {
			this.bootstrapService.manageModal('#unmarkPaymentModal', ModalActions.Hide);
			this.infoRef.modalType.set(ModalTypes.Failure);
			this.infoRef.message.set(BalancesMessagingConstants.UNMARK_PAYMENT_FAILURE());
			this.bootstrapService.manageModal('#unmarkPaymentInfoModal', ModalActions.Show);
		} finally {
			this.isSubmitting = false;
		}
	}

	//#endregion Form Submission

	//#region Modal Management

	public onCancel(): void {
		if (this.unmarkForm.dirty) {
			this.escConfirmationRef.modalType.set(ModalTypes.Question);
			this.escConfirmationRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
			this.bootstrapService.manageModal('#unmarkPaymentModal', ModalActions.Hide);
			this.bootstrapService.manageModal('#unmarkPaymentEscConfirmationModal', ModalActions.Show);
		} else {
			this.bootstrapService.manageModal('#unmarkPaymentModal', ModalActions.Hide);
			this.dirtyFormRegistry.unregister(this);
		}
	}

	public infoModalConfirmed(): void {
		this.dirtyFormRegistry.unregister(this);
	}

	public canDeactivate(): boolean | Promise<boolean> {
		return this.awaitLeaveConfirmation(this.unmarkForm.dirty && !this.submitSucceeded, () => {
			this.escConfirmationRef.modalType.set(ModalTypes.Question);
			this.escConfirmationRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
			this.bootstrapService.manageModal('#unmarkPaymentModal', ModalActions.Hide);
			this.bootstrapService.manageModal('#unmarkPaymentEscConfirmationModal', ModalActions.Show);
		});
	}

	public onEscConfirmed(): void {
		this.bootstrapService.manageModal('#unmarkPaymentEscConfirmationModal', ModalActions.Hide);
		this.dirtyFormRegistry.unregister(this);
		this.resolvePendingLeave(true);
	}

	public onEscCancelled(): void {
		this.bootstrapService.manageModal('#unmarkPaymentEscConfirmationModal', ModalActions.Hide);
		this.bootstrapService.manageModal('#unmarkPaymentModal', ModalActions.Show);
		this.resolvePendingLeave(false);
	}

	//#endregion Modal Management

	//#region Private Helpers

	private buildRequest(): UnmarkPropertyChargeRequest {
		const formValue = this.unmarkForm.value;
		return {
			note: formValue.note?.trim() || undefined,
			sendEmail: formValue.sendEmail
		};
	}

	private resetForm(): void {
		this.submitSucceeded = false;
		this.unmarkForm.reset({
			note: '',
			sendEmail: true
		});
	}

	//#endregion Private Helpers
}
